<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSubscription } from '@/hooks/useSubscription'
import { useI18n } from 'vue-i18n'
import api from '@/lib/axios'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

const { t } = useI18n()
const router = useRouter()
const {
  isTrialExpired,
  isPastDue,
  isCancelled,
  isPendingOffer,
  hasPendingOffer,
  isPublicMode,
  isOffersMode,
} = useSubscription()

const isSubmitting = ref(false)
const offerRequested = ref(false)

const requestOffer = async () => {
  isSubmitting.value = true
  try {
    await api.post('/api/v1/billing/request-offer')
    offerRequested.value = true
  } catch (error) {
    console.error('Failed to request offer:', error)
  } finally {
    isSubmitting.value = false
  }
}

const goToBilling = () => {
  router.push('/app/settings/billing')
}
</script>

<template>
  <!-- PUBLIC MODE: Trial Expired → Go choose a plan -->
  <Dialog :open="isTrialExpired && isPublicMode" @update:open="() => {}">
    <DialogContent class="sm:max-w-md" @interact-outside.prevent @escape-key-down.prevent :hideClose="true">
      <DialogHeader>
        <DialogTitle>{{ t('subscription.trial_expired_title', 'Trial Period Ended') }}</DialogTitle>
        <DialogDescription>
          {{ t('subscription.trial_expired_public_description', 'Your trial period has ended. Choose a plan to continue using the service.') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button @click="goToBilling">
          {{ t('subscription.choose_plan', 'Choose a Plan') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- OFFERS MODE: Trial Expired - Never paid, no offer yet -->
  <Dialog :open="isTrialExpired && isOffersMode && !hasPendingOffer && !offerRequested" @update:open="() => {}">
    <DialogContent class="sm:max-w-md" @interact-outside.prevent @escape-key-down.prevent :hideClose="true">
      <DialogHeader>
        <DialogTitle>{{ t('subscription.trial_expired_title', 'Trial Period Ended') }}</DialogTitle>
        <DialogDescription>
          {{ t('subscription.trial_expired_description', 'Your trial period has ended. If you would like to receive a subscription offer to continue using the service, click Accept.') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button @click="requestOffer" :disabled="isSubmitting">
          {{ isSubmitting
            ? t('subscription.requesting', 'Requesting...')
            : t('subscription.accept_offer', 'Accept')
          }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- OFFERS MODE: Offer requested confirmation -->
  <Dialog :open="offerRequested && isOffersMode" @update:open="() => {}">
    <DialogContent class="sm:max-w-md" @interact-outside.prevent @escape-key-down.prevent :hideClose="true">
      <DialogHeader>
        <DialogTitle>{{ t('subscription.offer_requested_title', 'Request Received') }}</DialogTitle>
        <DialogDescription>
          {{ t('subscription.offer_requested_description', 'We have received your request. Our team will contact you shortly with a personalized offer.') }}
        </DialogDescription>
      </DialogHeader>
    </DialogContent>
  </Dialog>

  <!-- OFFERS MODE: Pending Offer available -->
  <Dialog :open="(isTrialExpired || isPendingOffer) && isOffersMode && hasPendingOffer" @update:open="() => {}">
    <DialogContent class="sm:max-w-md" @interact-outside.prevent @escape-key-down.prevent :hideClose="true">
      <DialogHeader>
        <DialogTitle>{{ t('subscription.offer_available_title', 'Subscription Offer Available') }}</DialogTitle>
        <DialogDescription>
          {{ t('subscription.offer_available_description', 'You have a subscription offer available. Go to the billing section to review and subscribe.') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button @click="goToBilling">
          {{ t('subscription.go_to_billing', 'Go to Billing') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- BOTH MODES: Past Due / Cancelled -->
  <Dialog :open="isPastDue || isCancelled" @update:open="() => {}">
    <DialogContent class="sm:max-w-md" @interact-outside.prevent @escape-key-down.prevent :hideClose="true">
      <DialogHeader>
        <DialogTitle>
          {{ isPastDue
            ? t('subscription.past_due_title', 'Payment Required')
            : t('subscription.cancelled_title', 'Subscription Cancelled')
          }}
        </DialogTitle>
        <DialogDescription>
          {{ isPastDue
            ? t('subscription.past_due_description', 'Your payment could not be processed. Please update your billing information.')
            : t('subscription.cancelled_description', 'Your subscription has been cancelled. Please update your billing information to continue.')
          }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button @click="goToBilling">
          {{ t('subscription.go_to_billing', 'Go to Billing') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
